import { useEffect, useRef, useState } from "react";

type FieldValidityMap = Record<string, boolean>;
type ValidityField =
  | HTMLInputElement
  | HTMLSelectElement
  | HTMLTextAreaElement
  | null;
type FormValidityResult = {
  allValid: boolean;
  firstInvalidField: ValidityField;
};
type FormChildrenRender = (args: {
  fieldValidity: FieldValidityMap;
}) => React.ReactNode;

interface Props extends Omit<
  React.FormHTMLAttributes<HTMLFormElement>,
  "children"
> {
  children: React.ReactNode | FormChildrenRender;
  onSubmitInvalid?: () => void;
  isCustomValid?: () => boolean;
  encType?: string;
  useCustomValidation?: boolean;
}

function getFormValidity(formEl: HTMLFormElement | null): FormValidityResult {
  if (!formEl) {
    return { allValid: true, firstInvalidField: null };
  }

  const fields = formEl.querySelectorAll<
    HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
  >("input, select, textarea");
  for (const field of fields) {
    if (!field.checkValidity()) {
      return { allValid: false, firstInvalidField: field };
    }
  }

  let requiredRadioAndCheckboxesFieldsets;
  try {
    requiredRadioAndCheckboxesFieldsets = requiredRadioAndCheckboxesFieldsets =
      formEl?.querySelectorAll(
        "div:has(input[type=radio]).required, div:has(input[type=checkbox]).required",
      );
  } catch (error) {
    // Firefox doesn't support :has() selector
    requiredRadioAndCheckboxesFieldsets = requiredRadioAndCheckboxesFieldsets =
      formEl?.querySelectorAll("div.required");
  }

  const fieldsets =
    requiredRadioAndCheckboxesFieldsets as NodeListOf<HTMLFieldSetElement>;
  for (const fieldset of fieldsets) {
    let oneChecked = false;
    const inputs = fieldset.querySelectorAll(
      "input[type=radio], input[type=checkbox]",
    );
    inputs.forEach((radio) => {
      if ((radio as HTMLInputElement).checked) {
        oneChecked = true;
      }
    });
    const shouldBeValid =
      oneChecked || !requiredRadioAndCheckboxesFieldsets.length;
    if (!shouldBeValid) {
      (inputs[0] as HTMLInputElement).focus();
      window.scrollTo(0, fieldset.offsetTop);
      fieldset.scrollIntoView({
        behavior: "auto",
        block: "center",
        inline: "center",
      });
      return {
        allValid: false,
        firstInvalidField: inputs[0] as HTMLInputElement,
      };
    }
  }

  // https://bugs.chromium.org/p/chromium/issues/detail?id=1508784
  const patternedSelects = formEl.querySelectorAll("select[pattern]");
  for (const select of patternedSelects) {
    const pattern = select.getAttribute("pattern");
    if (pattern) {
      if (!(select as HTMLSelectElement).value.match(pattern)) {
        const descElAtr = select.getAttribute("aria-describedby");
        const descEl = descElAtr ? document.getElementById(descElAtr) : null;
        let scrollEl = select;
        if (descEl) {
          descEl.focus();
          scrollEl = descEl;
        } else {
          (select as HTMLSelectElement).focus();
        }
        window.scrollTo(0, (scrollEl as HTMLElement).offsetTop);
        scrollEl.scrollIntoView({
          behavior: "auto",
          block: "center",
          inline: "center",
        });
        return {
          allValid: false,
          firstInvalidField: select as HTMLSelectElement,
        };
      }
    }
  }

  return { allValid: true, firstInvalidField: null };
}

function SebkortForm(props: Readonly<Props>) {
  const {
    children,
    onSubmit: propOnSubmit,
    onClickCapture: propOnClickCapture,
    onSubmitInvalid,
    isCustomValid,
    useCustomValidation,
    ...otherProps
  } = props;

  const formRef = useRef<HTMLFormElement>(null);
  const [fieldValidity, setFieldValidity] = useState<FieldValidityMap>({});
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const getNamedFields = () => {
    return (
      (formRef.current as unknown as HTMLFormElement)?.querySelectorAll<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >("input[name], select[name], textarea[name]") || []
    );
  };

  const syncAllFieldValidity = () => {
    const nextFieldValidity: FieldValidityMap = {};
    getNamedFields().forEach((field) => {
      const name = field.getAttribute("name");
      if (!name) {
        return;
      }
      nextFieldValidity[name] = field.checkValidity();
    });
    setFieldValidity(nextFieldValidity);
  };

  const initializeFieldValidityAsValid = () => {
    const nextFieldValidity: FieldValidityMap = {};
    getNamedFields().forEach((field) => {
      const name = field.getAttribute("name");
      if (!name) {
        return;
      }
      nextFieldValidity[name] = true;
    });
    setFieldValidity(nextFieldValidity);
  };

  useEffect(() => {
    if (!hasSubmitted) {
      initializeFieldValidityAsValid();
    }
    // children can change as form steps/renders change, keep pre-submit state valid
  }, [children, hasSubmitted]);

  const onFieldInteraction = (e: React.SyntheticEvent<HTMLFormElement>) => {
    if (!hasSubmitted) {
      return;
    }

    const target = e.target as
      | HTMLInputElement
      | HTMLSelectElement
      | HTMLTextAreaElement
      | null;

    if (!target) {
      return;
    }

    const name = target.getAttribute("name");
    if (!name) {
      return;
    }

    setFieldValidity((prev) => ({
      ...prev,
      [name]: target.checkValidity(),
    }));
  };

  const onClickCapture = (e: React.MouseEvent<HTMLFormElement>) => {
    const target = e.target as Element | null;
    if (!target) {
      propOnClickCapture?.(e);
      return;
    }

    const submitTrigger = target.closest("button, input");
    if (!submitTrigger) {
      propOnClickCapture?.(e);
      return;
    }

    const tagName = submitTrigger.tagName.toLowerCase();

    if (tagName === "button") {
      const type = (
        submitTrigger.getAttribute("type") || "submit"
      ).toLowerCase();
      if (type !== "submit") {
        propOnClickCapture?.(e);
        return;
      }
    }

    if (tagName === "input") {
      const type = (
        (submitTrigger as HTMLInputElement).type || "text"
      ).toLowerCase();
      if (type !== "submit") {
        propOnClickCapture?.(e);
        return;
      }
    }

    setHasSubmitted(true);
    syncAllFieldValidity();
    propOnClickCapture?.(e);
    const { allValid, firstInvalidField } = getFormValidity(
      formRef.current as unknown as HTMLFormElement,
    );
    if (!allValid) {
      e.preventDefault();
      firstInvalidField?.focus();
    }
  };

  /* - html5 validation, plus adds some enhancements where the native validation lacks.
      At the time of writing not all browsers supported:
        1) required radioboxes - it would ask to select all of them or not give the user any
            information about what he/she was lacking when none where default checked an a user
            tried to submit the form without selecting any. <- seems fixed now but keeping for backwards compatibility - (older than supported)
        2) pattern attribute not supported for selectboxes <- still needed as of writing 2023-12-11 in Chrome + all others
  */
  const onSubmit = (e: React.SubmitEvent<HTMLFormElement>) => {
    setHasSubmitted(true);
    syncAllFieldValidity();
    const { allValid } = getFormValidity(
      formRef.current as unknown as HTMLFormElement,
    );
    if (
      (allValid && typeof isCustomValid === "undefined") ||
      (allValid && isCustomValid && isCustomValid())
    ) {
      if (propOnSubmit) {
        propOnSubmit(e);
      }
    } else {
      onSubmitInvalid && onSubmitInvalid();
      e.preventDefault();
    }
  };

  let customValidationFormProps = {};
  if (useCustomValidation) {
    customValidationFormProps = {
      //   noValidate: true,
      onClickCapture: onClickCapture,
      onBlurCapture: onFieldInteraction,
      onInputCapture: onFieldInteraction,
    };
  }

  return (
    <form
      ref={formRef}
      onSubmit={onSubmit}
      {...customValidationFormProps}
      {...otherProps}
      style={{ paddingBottom: "2rem" }}
    >
      {typeof children === "function" ? children({ fieldValidity }) : children}
    </form>
  );
}

export default SebkortForm;
